import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Activity, TrendingUp, Sparkles } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MacroProgressBar = ({ label, current, color, unit = 'g' }) => {
  // No target-based percentage, just show the amount consumed
  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <span className="font-medium text-gray-700">{label}</span>
        <span className={`font-bold ${color}`}>{current.toFixed(1)}{unit}</span>
      </div>
      <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${Math.min((current / 200) * 100, 100)}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={`h-full rounded-full ${
            label === 'Protein' ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
            label === 'Carbohydrates' ? 'bg-gradient-to-r from-amber-400 to-orange-500' :
            'bg-gradient-to-r from-green-400 to-emerald-500'
          }`}
        />
      </div>
    </div>
  );
};

const BiometricsPage = () => {
  const { token } = useAuth();
  const [todaysMacros, setTodaysMacros] = useState({ protein: 0, carbs: 0, fat: 0, calories: 0 });
  const [todaysMicros, setTodaysMicros] = useState({ vitamin_b12: 0, calcium: 0, folate: 0, iron: 0 });
  const [hasMicrosData, setHasMicrosData] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTodaysNutrition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTodaysNutrition = async () => {
    try {
      setLoading(true);
      // Get today's date in YYYY-MM-DD format
      const today = format(new Date(), 'yyyy-MM-dd');
      
      // Fetch food entries for today
      const response = await axios.get(`${API_URL}/api/food-entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter for today's entries and calculate totals
      const todaysEntries = response.data.filter(entry => {
        if (!entry.created_at) return false;
        const entryDate = entry.created_at.split('T')[0];
        return entryDate === today && entry.is_finalized;
      });

      // Calculate macro totals
      const macroTotals = todaysEntries.reduce((acc, entry) => {
        return {
          protein: acc.protein + (entry.protein_g || 0),
          carbs: acc.carbs + (entry.carbs_g || 0),
          fat: acc.fat + (entry.fat_g || 0),
          calories: acc.calories + (entry.calories || 0)
        };
      }, { protein: 0, carbs: 0, fat: 0, calories: 0 });

      // Calculate micro totals (only from entries that have been analyzed)
      const entriesWithMicros = todaysEntries.filter(entry => entry.micros_analyzed);
      const microTotals = entriesWithMicros.reduce((acc, entry) => {
        return {
          vitamin_b12: acc.vitamin_b12 + (entry.vitamin_b12_mcg || 0),
          calcium: acc.calcium + (entry.calcium_mg || 0),
          folate: acc.folate + (entry.folate_mcg || 0),
          iron: acc.iron + (entry.iron_mg || 0)
        };
      }, { vitamin_b12: 0, calcium: 0, folate: 0, iron: 0 });

      setTodaysMacros(macroTotals);
      setTodaysMicros(microTotals);
      setHasMicrosData(entriesWithMicros.length > 0);
    } catch (error) {
      console.error('Error fetching today\'s nutrition:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Today's Progress
        </h2>
        <p className="text-muted-foreground">
          Track your daily macronutrient intake
        </p>
      </motion.div>

      {/* Today's Progress Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
        className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Today's Macros</h3>
            <p className="text-sm text-gray-500">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-2">
            <MacroProgressBar 
              label="Protein" 
              current={todaysMacros.protein} 
              color="text-blue-600"
            />
            <MacroProgressBar 
              label="Carbohydrates" 
              current={todaysMacros.carbs} 
              color="text-amber-600"
            />
            <MacroProgressBar 
              label="Fat" 
              current={todaysMacros.fat} 
              color="text-green-600"
            />

            {/* Summary */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="p-4 bg-blue-50 rounded-xl">
                  <p className="text-2xl font-bold text-blue-600">{todaysMacros.protein.toFixed(1)}g</p>
                  <p className="text-xs text-blue-500 uppercase tracking-wide mt-1">Protein</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl">
                  <p className="text-2xl font-bold text-amber-600">{todaysMacros.carbs.toFixed(1)}g</p>
                  <p className="text-xs text-amber-500 uppercase tracking-wide mt-1">Carbs</p>
                </div>
                <div className="p-4 bg-green-50 rounded-xl">
                  <p className="text-2xl font-bold text-green-600">{todaysMacros.fat.toFixed(1)}g</p>
                  <p className="text-xs text-green-500 uppercase tracking-wide mt-1">Fat</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-xl">
                  <p className="text-2xl font-bold text-purple-600">{todaysMacros.calories.toFixed(0)}</p>
                  <p className="text-xs text-purple-500 uppercase tracking-wide mt-1">Calories</p>
                </div>
              </div>
            </div>

            {/* Micronutrients Section */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-800">Today's Micronutrients</h4>
                  <p className="text-xs text-gray-500">Vitamins & minerals from analyzed meals</p>
                </div>
              </div>

              {hasMicrosData ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  <div className="p-4 bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl border border-rose-100">
                    <p className="text-xl font-bold text-rose-600">{todaysMicros.vitamin_b12.toFixed(1)}</p>
                    <p className="text-xs text-rose-500 uppercase tracking-wide mt-1">Vitamin B12 (mcg)</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-cyan-50 to-sky-50 rounded-xl border border-cyan-100">
                    <p className="text-xl font-bold text-cyan-600">{todaysMicros.calcium.toFixed(1)}</p>
                    <p className="text-xs text-cyan-500 uppercase tracking-wide mt-1">Calcium (mg)</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                    <p className="text-xl font-bold text-emerald-600">{todaysMicros.folate.toFixed(1)}</p>
                    <p className="text-xs text-emerald-500 uppercase tracking-wide mt-1">Folate (mcg)</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100">
                    <p className="text-xl font-bold text-orange-600">{todaysMicros.iron.toFixed(1)}</p>
                    <p className="text-xs text-orange-500 uppercase tracking-wide mt-1">Iron (mg)</p>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-gray-50 rounded-xl text-center">
                  <p className="text-sm text-gray-500">
                    No micronutrient data available yet. Click on your food images in the Food Diary to get detailed vitamin & mineral analysis.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>

      {/* Info Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100"
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <Activity className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h4 className="font-semibold text-green-800 mb-1">About Your Progress</h4>
            <p className="text-sm text-green-700">
              This chart shows the total amount of protein, carbohydrates, and fat you've consumed today 
              from your finalized meals. Keep logging your meals to track your daily intake!
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BiometricsPage;
